import { motion } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Check, X as XIcon, Circle } from "@/components/ui/icons";
import { FirmLogo } from "@/components/firm-logo";
import { cn } from "@/lib/utils";

/**
 * Operator-facing representation of one row after Accounts.tsx maps
 * `AccountRow` → view-model. Keep nullable fields narrow: everything
 * needed to render a grid card is on here, but nothing else (the
 * detail page owns the full rule-set + history).
 */
export interface AccountCardData {
  id: string;
  slug: string;
  pairSlug: string | null;
  firmId: string;
  firmName: string;
  firmLogo: string;
  login: string | null;
  accountLabel: string | null;
  accountSize: number;
  balance: number | null;
  finalBalance: number | null;
  currency: string;
  phase: string;
  /** Raw challenge_type from D1: "instant" | "1-step" | "2-step" |
   *  "3-step" | "evaluation" | "other" | null. Drives the small chip
   *  next to the firm name so an instant funded card never looks like
   *  a step-1 evaluation card. */
  challengeType: string | null;
  /** Computed by the caller — one of 'active' | 'paused' | 'passed' |
   *  'breached' | 'pending' | 'validating' | 'failed_validation'. */
  status: string;
  /** Optional domain-specific status label, e.g. instant funding
   *  outcome='passed' is a payout rather than a challenge pass. */
  statusLabel?: string;
  rulesConfirmedAt: number | null;
  outcome: "passed" | "breached" | null;
  outcomeAt: number | null;
}

function formatMoney(n: number, currency = "USD"): string {
  if (!Number.isFinite(n)) return `— ${currency}`;
  return `${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: n < 100 ? 2 : 0,
  })} ${currency}`;
}

function phaseBadgeVariant(
  phase: string,
): "step1" | "step2" | "funded" | "warning" {
  if (phase === "step1") return "step1";
  if (phase === "step2") return "step2";
  if (phase === "funded") return "funded";
  return "warning";
}

function phaseLabel(phase: string): string {
  if (phase === "step1") return "Step 1";
  if (phase === "step2") return "Step 2";
  if (phase === "step3") return "Step 3";
  if (phase === "funded") return "Funded";
  return phase;
}

/**
 * Compact label for the challenge type chip. Returns null when the
 * field is missing so the caller can drop the chip entirely instead
 * of rendering an empty pill.
 */
function challengeTypeLabel(ct: string | null): string | null {
  if (!ct) return null;
  const v = ct.toLowerCase();
  if (v === "instant") return "Instant";
  if (v === "1-step") return "1-Step";
  if (v === "2-step") return "2-Step";
  if (v === "3-step") return "3-Step";
  return null;
}

type StatusVariant =
  | "active"
  | "paused"
  | "passed"
  | "breached"
  | "warning"
  | "default";

function statusBadgeProps(
  status: string,
  labelOverride?: string,
): { variant: StatusVariant; label: string; icon: "check" | "x" | "dot" } {
  switch (status) {
    case "passed":
      return { variant: "passed", label: labelOverride ?? "Passed", icon: "check" };
    case "breached":
      return { variant: "breached", label: labelOverride ?? "Breached", icon: "x" };
    case "paused":
      return { variant: "paused", label: "Paused", icon: "dot" };
    case "validating":
      return { variant: "warning", label: "Validating", icon: "dot" };
    case "pending":
      return { variant: "warning", label: "Pending", icon: "dot" };
    case "failed_validation":
      return { variant: "breached", label: "Failed", icon: "x" };
    default:
      return { variant: "active", label: "Active", icon: "dot" };
  }
}

/**
 * Grid card for the Accounts page. Archived accounts pass
 * `muted=true` — we keep the same layout but soften everything and
 * swap live balance for the frozen `finalBalance` captured at
 * Mark Passed / Mark Breached time.
 */
export function AccountCard({
  data,
  muted = false,
}: {
  data: AccountCardData;
  muted?: boolean;
}) {
  const navigate = useNavigate();
  const balance = muted ? data.finalBalance : data.balance;
  const status = statusBadgeProps(data.status, data.statusLabel);
  const ctLabel = challengeTypeLabel(data.challengeType);
  const rulesPending = data.rulesConfirmedAt == null && !muted;

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => navigate(`/accounts/${encodeURIComponent(data.slug)}`)}
      className={cn(
        "group relative flex w-full flex-col gap-2 rounded-xl border border-border/40 bg-card p-3 text-left transition-all hover:border-accent/40 hover:bg-card/80 hover:shadow-md",
        muted && "opacity-70",
      )}
    >
      {/* ── Row 1: firm + nickname ───────────────────────── */}
      <div className="flex items-center gap-2">
        <FirmLogo
          src={data.firmLogo}
          name={data.firmName}
          className="h-5 w-5 shrink-0"
        />
        <span
          className={cn(
            "truncate text-xs font-medium",
            muted ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {data.firmName}
        </span>
        {data.accountLabel && (
          <span className="truncate rounded-full border border-accent/30 bg-accent/5 px-1.5 py-0 font-mono text-[10px] text-accent">
            {data.accountLabel}
          </span>
        )}
        {ctLabel && (
          <span className="shrink-0 rounded-full border border-border/50 bg-muted/30 px-1.5 py-0 font-mono text-[10px] text-muted-foreground">
            {ctLabel}
          </span>
        )}
      </div>

      {/* ── Row 2: login + balance ───────────────────────── */}
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            "truncate font-mono text-[11px]",
            muted && "line-through",
            data.rulesConfirmedAt == null && !muted && "text-amber-500",
            data.rulesConfirmedAt != null && "text-muted-foreground",
          )}
          title={data.login ?? undefined}
        >
          #{data.login ?? data.slug}
        </span>
        <span className="ml-auto shrink-0 font-mono text-sm font-semibold text-accent">
          {balance != null
            ? formatMoney(balance, data.currency)
            : formatMoney(data.accountSize, data.currency)}
        </span>
      </div>

      {/* ── Row 3: phase + status + pair ─────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={phaseBadgeVariant(data.phase)} size="xs">
          {phaseLabel(data.phase)}
        </Badge>
        <Badge variant={status.variant} size="xs">
          {status.icon === "check" ? (
            <Check className="size-2.5" strokeWidth={3} />
          ) : status.icon === "x" ? (
            <XIcon className="size-2.5" strokeWidth={3} />
          ) : (
            <Circle className="size-2 fill-current" strokeWidth={0} />
          )}
          {status.label}
        </Badge>
        {rulesPending && (
          <Badge variant="warning" size="xs" title="Rules not confirmed yet — open the account to fill them in.">
            Rules Pending
          </Badge>
        )}
        {data.pairSlug && (
          <Link
            to={`/pairs/${encodeURIComponent(data.pairSlug)}`}
            onClick={(e) => e.stopPropagation()}
            className="ml-auto inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 font-mono text-[10px] text-accent transition-colors hover:bg-accent/20"
          >
            {data.pairSlug}
          </Link>
        )}
      </div>

    </motion.button>
  );
}
